import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromStrategy from '@strategy/reducers';

import { Company, Notification } from '@shared/models/Strategy';
import { TrackingService } from '@shared/services/tracking.service';
import { PersonaliseTabs } from '@strategy/components';

@Component({
  selector: 'app-strategy-company-actions',
  templateUrl: './strategy-company-actions.component.html',
  // TODO: Split scss files by component
  styleUrls: ['../strategy-personalise/strategy-personalise.component.scss'],
  standalone: false
})
export class StrategyCompanyActionsComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  @Input() activeTab: string;
  @Input() blockedCompaniesIDs = [];
  @Input() company: Company;
  @Input() listType = '';
  @Input() selectedCompanyID = 0;
  @Input() location: 'list' | 'companyDetail';
  @Input() mobileView = false;
  @Input() showExcludedAction = true;
  @Input() wishedCompaniesIDs = [];
  @Output() updateLoadingID = new EventEmitter<number>();

  portfolioCompanyListIDs: { wishedCompanyIDs: number[]; blockedCompanyIDs: number[] } = null;

  constructor(
    private store: Store,
    public trackingService: TrackingService
  ) {
    this.store.pipe(select(fromStrategy.selectPortfolioCompanyListIDs), takeUntil(this.onDestroy$)).subscribe((companyIDs) => {
      this.portfolioCompanyListIDs = companyIDs;
    });
  }

  updateWishStatus(event: MouseEvent, company: Company, action: Notification['type']): void {
    event.stopPropagation();

    this.updateLoadingID.emit(company.id);

    if (action === 'clearWish') {
      const index = this.portfolioCompanyListIDs.wishedCompanyIDs.indexOf(company.id);
      this.portfolioCompanyListIDs.wishedCompanyIDs.splice(index, 1);
      this.trackingService.trackActivity(
        `[Button] Removed from wishlist - ${this.getLeadActivityLocation(this.activeTab as PersonaliseTabs)}: ${company.name}`
      );
    } else {
      // clear from blocked
      const blockedIndex = this.portfolioCompanyListIDs.blockedCompanyIDs.indexOf(company.id);
      if (blockedIndex !== -1) {
        this.portfolioCompanyListIDs.blockedCompanyIDs.splice(blockedIndex, 1);
      }

      this.portfolioCompanyListIDs.wishedCompanyIDs.push(company.id);
      this.trackingService.trackActivity(
        `[Button] Added to wishlist - ${this.getLeadActivityLocation(this.activeTab as PersonaliseTabs)}: ${company.name}`
      );
    }

    this.store.dispatch(StrategyActions.setNotification({ notification: { company: company.name, type: action, show: false } }));
    this.store.dispatch(StrategyActions.updatePortfolioCompanyList(this.portfolioCompanyListIDs));
  }

  updateBlockedStatus(event: MouseEvent, company: Company, action: Notification['type']): void {
    event.stopPropagation();

    this.updateLoadingID.emit(company.id);

    if (action === 'clearBlocked') {
      const index = this.portfolioCompanyListIDs.blockedCompanyIDs.indexOf(company.id);
      this.portfolioCompanyListIDs.blockedCompanyIDs.splice(index, 1);
      this.trackingService.trackActivity(
        `[Button] Removed from blacklist - ${this.getLeadActivityLocation(this.activeTab as PersonaliseTabs)}: ${company.name}`
      );
    } else {
      // clear from wished
      const wishedIndex = this.portfolioCompanyListIDs.wishedCompanyIDs.indexOf(company.id);
      if (wishedIndex !== -1) {
        this.portfolioCompanyListIDs.wishedCompanyIDs.splice(wishedIndex, 1);
      }

      this.portfolioCompanyListIDs.blockedCompanyIDs.push(company.id);
      this.trackingService.trackActivity(
        `[Button] Added to blacklist - ${this.getLeadActivityLocation(this.activeTab as PersonaliseTabs)}: ${company.name}`
      );
    }

    this.store.dispatch(StrategyActions.setNotification({ notification: { company: company.name, type: action, show: false } }));
    this.store.dispatch(StrategyActions.updatePortfolioCompanyList(this.portfolioCompanyListIDs));
  }

  isWishedCompany(companyID: number): boolean {
    return this.wishedCompaniesIDs.includes(companyID);
  }

  isBlockedCompany(companyID: number): boolean {
    return this.blockedCompaniesIDs.includes(companyID);
  }

  getLeadActivityLocation(activeTab: PersonaliseTabs): string {
    switch (activeTab) {
      case PersonaliseTabs.PORTFOLIO:
        return 'Your portfolio';
      case PersonaliseTabs.RECOMMENDATIONS:
        return 'You might also like';
      case PersonaliseTabs.SEARCH:
        return 'Search';
      default:
        return '';
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
